import { ValidatorSchema } from "@constants/validators";
import { Currency } from "@types";
import { MemberSettingNameMode } from "kz-ui-sdk";
import { z } from "zod";
import { BankFormConfig, EWalletFormConfig } from "../types";

const isEnabled = (flag?: boolean): boolean => flag === true;

export const buildNameFieldsSchema = (
  config: BankFormConfig | EWalletFormConfig,
  nameMode: MemberSettingNameMode | null | undefined,
  legalName: string | null | undefined,
): Record<string, z.ZodTypeAny> => {
  const nameFields: Record<string, z.ZodTypeAny> = {};

  if (!legalName) {
    if (nameMode === MemberSettingNameMode.FullInFirst && isEnabled(config?.fullName?.enabled)) {
      nameFields.accountName = ValidatorSchema.accountName;
    }
    if (nameMode === MemberSettingNameMode.FirstLast) {
      if (isEnabled(config?.firstName?.enabled)) nameFields.firstName = ValidatorSchema.firstName;
      if (isEnabled(config?.lastName?.enabled)) nameFields.lastName = ValidatorSchema.lastName;
    }
  }

  return nameFields;
};

export const buildUniqueIdentifierFieldsSchema = (
  config: BankFormConfig | EWalletFormConfig,
): Record<string, z.ZodTypeAny> => {
  const extraFields: Record<string, z.ZodTypeAny> = {};

  if (isEnabled(config?.pan?.enabled)) {
    extraFields.pan = ValidatorSchema.pan;
  }
  if (isEnabled(config?.cpf?.enabled)) {
    extraFields.cpf = ValidatorSchema.cpf;
  }
  if (isEnabled(config?.curp?.enabled)) {
    extraFields.curp = ValidatorSchema.curp;
  }

  return extraFields;
};

export const buildPidSchema = (config: BankFormConfig | EWalletFormConfig, currency: Currency): z.ZodTypeAny | null => {
  const isPEN = currency === Currency.PEN;
  const isCOP = currency === Currency.COP;

  if (!(isPEN || isCOP) || !isEnabled(config?.pid?.enabled)) {
    return null;
  }

  const pidTypeSubSchemas = [
    z.object({ pidType: z.literal("dni"), pidNumber: ValidatorSchema.pid.dni }),
    z.object({ pidType: z.literal("pen_pas"), pidNumber: ValidatorSchema.pid.pen_pas }),
    z.object({ pidType: z.literal("ruc"), pidNumber: ValidatorSchema.pid.ruc }),
    z.object({ pidType: z.literal("cc"), pidNumber: ValidatorSchema.pid.cc }),
    z.object({ pidType: z.literal("nit"), pidNumber: ValidatorSchema.pid.nit }),
    z.object({ pidType: z.literal("cop_pas"), pidNumber: ValidatorSchema.pid.cop_pas }),
  ] as unknown as [z.ZodDiscriminatedUnionOption<"pidType">, ...z.ZodDiscriminatedUnionOption<"pidType">[]];

  if (isPEN) {
    pidTypeSubSchemas.push(z.object({ pidType: z.literal("ce"), pidNumber: ValidatorSchema.pid.pen_ce }));
  }

  if (isCOP) {
    pidTypeSubSchemas.push(z.object({ pidType: z.literal("ce"), pidNumber: ValidatorSchema.pid.cop_ce }));
  }

  return z.discriminatedUnion("pidType", pidTypeSubSchemas, {
    errorMap: () => {
      return { message: "required" };
    },
  });
};
